import { APP_ROUTES, TORNADO_V2_ROUTES } from '@zorro/common-utils';
import { NextRouter, useRouter as useNextRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { useLoadingOverlay } from '.';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RouteConfig = Record<string, (...params: any[]) => string>;

type UseDynamicRouter = <R extends RouteConfig>(
  routeConfig: R
) => {
  router: NextRouter;
  isNavigating: boolean;
  isNavigatingToRoute: <K extends keyof R>(
    route: K,
    params: Parameters<R[K]>
  ) => boolean;
  reloadPage: (options?: { scroll: boolean }) => void;
  navigateToRoute: <K extends keyof R>(
    route: K,
    params: Parameters<R[K]>,
    queryParams?: Record<string, string>,
    shouldReplace?: boolean,
    shouldPreserveQueryParams?: boolean
  ) => void;
  navigateToRouteAsync: <K extends keyof R>(
    route: K,
    params: Parameters<R[K]>,
    queryParams?: Record<string, string>,
    shouldReplace?: boolean,
    shouldPreserveQueryParams?: boolean
  ) => Promise<boolean>;
};

export const useDynamicRouter: UseDynamicRouter = <R extends RouteConfig>(
  routeConfig: R
) => {
  const [loadingState, setLoadingState] = useState<Record<string, boolean>>({});
  const { stopLoading } = useLoadingOverlay();
  const router = useNextRouter();

  const handleSetLoadingState = (pathname: string) => {
    const pathWithoutQueryParams = pathname.split('?')[0];
    setLoadingState((prev) => ({ ...prev, [pathWithoutQueryParams]: true }));
  };

  const handleClearLoadingState = () => {
    setLoadingState({});
    stopLoading();
  };

  const handleGetLoadingState = <K extends keyof R>(
    route: K,
    params: Parameters<R[K]>
  ): boolean => {
    const path = routeConfig[route](...params);
    return loadingState[path] ?? false;
  };

  const navigateToRouteAsync = <K extends keyof R>(
    route: K,
    params: Parameters<R[K]>,
    queryParams: Record<string, string> = {},
    shouldReplace = false,
    shouldPreserveQueryParams = true
  ) => {
    const url = new URL(window.location.href);
    const currentQueryParams = shouldPreserveQueryParams
      ? Object.fromEntries(url.searchParams)
      : {};
    const routerAction = shouldReplace ? router.replace : router.push;

    return routerAction({
      pathname: routeConfig[route](...params),
      query: { ...currentQueryParams, ...queryParams },
    });
  };

  const navigateToRoute = <K extends keyof R>(
    route: K,
    params: Parameters<R[K]>,
    queryParams: Record<string, string> = {},
    shouldReplace = false,
    shouldPreserveQueryParams = true
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigateToRouteAsync(
      route,
      params,
      queryParams,
      shouldReplace,
      shouldPreserveQueryParams
    );
  };

  const reloadPage = (options?: { scroll: boolean }) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace(router.asPath, undefined, { ...options });
  };

  useEffect(() => {
    router.events.on('routeChangeStart', handleSetLoadingState);
    router.events.on('routeChangeComplete', handleClearLoadingState);
    router.events.on('routeChangeError', handleClearLoadingState);

    return () => {
      router.events.off('routeChangeStart', handleSetLoadingState);
      router.events.off('routeChangeComplete', handleClearLoadingState);
      router.events.off('routeChangeError', handleClearLoadingState);
    };
  }, []);

  return {
    router,
    navigateToRoute,
    navigateToRouteAsync,
    isNavigating: Object.keys(loadingState).length > 0,
    isNavigatingToRoute: handleGetLoadingState,
    reloadPage,
  };
};

export const useDynamicTornadoRouter = () => {
  return useDynamicRouter(TORNADO_V2_ROUTES);
};
export const useAppRouter = () => {
  return useDynamicRouter(APP_ROUTES);
};
